// 리전 감지 서비스
// 브라우저: API 기반, 서버: geoip-lite 기반 IP 리전 감지

import { getLogger } from '@/lib/logger/logger';
import {
  REGION_CACHE,
  REGION_API,
  REGION_STORAGE_KEYS,
  SUPPORTED_REGIONS,
  type SupportedRegionCode,
} from '@/shared/constants/region';
import { retry, isNetworkError } from '@/shared/utils/retry';

const logger = getLogger('regionDetector');

export interface RegionInfo {
  countryCode: string; // 'KR', 'US', 'JP', 'GB', 'CN' 등
  isOverseas: boolean; // 해외 여부 (KR이 아닌 경우 true)
  detectedAt: Date; // 감지 시간
}

// Re-export for backward compatibility
export { SUPPORTED_REGIONS, type SupportedRegionCode };

// API 응답 타입
interface DetectRegionResponse {
  countryCode?: string;
}

class RegionDetectorService {
  private readonly STORAGE_KEY = REGION_STORAGE_KEYS.TEST;
  private readonly CACHE_KEY = REGION_STORAGE_KEYS.CACHE;
  private readonly DEV_DELAY_KEY = 'dev_region_delay';
  // ✅ FIX: localStorage를 사용하여 영구 캐시 적용 (새로고침 시에도 유지)
  private readonly USE_LOCAL_STORAGE = true;
  private userIP: string | null = null;

  /**
   * 현재 리전 정보를 가져옵니다.
   * 우선순위: localStorage (테스트) > sessionStorage (캐시) > 기본값 (KR)
   * @param forceRefresh - 캐시를 무시하고 강제로 재감지할지 여부
   */
  async detectRegion(forceRefresh = false): Promise<RegionInfo> {
    logger.info('[RegionDetector] 리전 감지 시작...', { forceRefresh });

    // 개발 환경에서 delay 시뮬레이션 (깜빡임 확인용)
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      const delayMs = this.getDevDelay();
      if (delayMs > 0) {
        logger.warn(`[RegionDetector] 개발용 delay 적용: ${delayMs}ms`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }

    // 1. 테스트 모드에서 설정한 리전 확인
    const testRegion = this.getTestRegion();
    if (testRegion) {
      logger.info(
        `[RegionDetector] 테스트 모드 리전 사용: ${testRegion.countryCode} (${testRegion.isOverseas ? '해외' : '국내'})`,
      );
      return testRegion;
    }

    // 2. 캐시된 리전 정보 확인 (강제 새로고침이 아닌 경우에만)
    if (!forceRefresh) {
      const cachedRegion = this.getCachedRegion();
      if (cachedRegion && this.isCacheValid(cachedRegion)) {
        logger.info(
          `[RegionDetector] 캐시된 리전 사용: ${cachedRegion.countryCode} (${cachedRegion.isOverseas ? '해외' : '국내'})`,
        );
        return cachedRegion;
      }
    }

    // 3. 실제 IP 기반 감지
    logger.info('[RegionDetector] IP 기반 리전 감지 중...');
    const detectedRegion = await this.detectFromIP();

    // 4. 캐시에 저장 (API 성공 시만)
    if (detectedRegion !== null) {
      this.setCachedRegion(detectedRegion);
      logger.info(
        `[RegionDetector] 감지 완료 및 캐시 저장: ${detectedRegion.countryCode} (${detectedRegion.isOverseas ? '해외' : '국내'})`,
      );
      return detectedRegion;
    }

    // API 실패: 해외 기본값(EN) 반환, 캐시 저장 안 함 → 다음 로딩에서 재시도
    // 해외 사용자가 한국어를 보는 것보다 국내 사용자가 영어를 보는 것이 더 안전
    logger.info('[RegionDetector] API 실패. 해외 기본값(EN) 사용, 캐시 저장 안 함.');
    return { countryCode: 'UNKNOWN', isOverseas: true, detectedAt: new Date() };
  }

  /**
   * 테스트용 리전 설정
   */
  setTestRegion(countryCode: SupportedRegionCode): void {
    const regionInfo: RegionInfo = {
      countryCode,
      isOverseas: countryCode !== SUPPORTED_REGIONS.KOREA,
      detectedAt: new Date(),
    };

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(regionInfo));
    // 캐시도 갱신
    this.setCachedRegion(regionInfo);
  }

  /**
   * 테스트 리전 설정 제거
   */
  clearTestRegion(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    // ✅ FIX: 캐시 저장소에서도 삭제
    this.getStorage().removeItem(this.CACHE_KEY);
  }

  /**
   * 캐시만 초기화 (강제 새로고침)
   */
  clearCache(): void {
    // ✅ FIX: 캐시 저장소에서 삭제
    this.getStorage().removeItem(this.CACHE_KEY);
    logger.info('[RegionDetector] 리전 캐시가 초기화되었습니다.');
  }

  /**
   * 개발 환경에서 region detection delay 설정 (깜빡임 확인용)
   */
  setDevDelay(delayMs: number): void {
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      localStorage.setItem(this.DEV_DELAY_KEY, delayMs.toString());
      logger.info(`[RegionDetector] 개발용 delay 설정: ${delayMs}ms`);
    }
  }

  /**
   * 개발 환경 delay 제거
   */
  clearDevDelay(): void {
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      localStorage.removeItem(this.DEV_DELAY_KEY);
      logger.info('[RegionDetector] 개발용 delay 제거');
    }
  }

  /**
   * 현재 설정된 개발용 delay 가져오기
   */
  private getDevDelay(): number {
    if (typeof window === 'undefined') return 0;
    const stored = localStorage.getItem(this.DEV_DELAY_KEY);
    if (!stored) return 0;
    const parsed = parseInt(stored, 10);
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * 현재 설정된 테스트 리전 가져오기
   */
  private getTestRegion(): RegionInfo | null {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (!stored) return null;

    try {
      const parsed = JSON.parse(stored) as Partial<RegionInfo>;
      return {
        countryCode: (parsed.countryCode as SupportedRegionCode) ?? SUPPORTED_REGIONS.KOREA,
        isOverseas:
          typeof parsed.isOverseas === 'boolean'
            ? parsed.isOverseas
            : (parsed.countryCode as SupportedRegionCode) !== SUPPORTED_REGIONS.KOREA,
        detectedAt: new Date(parsed.detectedAt as unknown as string),
      };
    } catch {
      return null;
    }
  }

  /**
   * 캐시 저장소 가져오기 (localStorage 또는 sessionStorage)
   */
  private getStorage(): Storage {
    return this.USE_LOCAL_STORAGE ? localStorage : sessionStorage;
  }

  /**
   * 캐시된 리전 정보 가져오기
   * ✅ FIX: localStorage를 사용하여 새로고침 시에도 캐시 유지
   */
  private getCachedRegion(): RegionInfo | null {
    const storage = this.getStorage();
    const cached = storage.getItem(this.CACHE_KEY);
    if (!cached) return null;

    try {
      const parsed = JSON.parse(cached) as Partial<RegionInfo>;
      return {
        countryCode: (parsed.countryCode as SupportedRegionCode) ?? SUPPORTED_REGIONS.KOREA,
        isOverseas:
          typeof parsed.isOverseas === 'boolean'
            ? parsed.isOverseas
            : (parsed.countryCode as SupportedRegionCode) !== SUPPORTED_REGIONS.KOREA,
        detectedAt: new Date(parsed.detectedAt as unknown as string),
      };
    } catch {
      return null;
    }
  }

  /**
   * 리전 정보를 캐시에 저장
   * ✅ FIX: localStorage를 사용하여 새로고침 시에도 캐시 유지
   */
  private setCachedRegion(regionInfo: RegionInfo): void {
    const storage = this.getStorage();
    storage.setItem(this.CACHE_KEY, JSON.stringify(regionInfo));
  }

  /**
   * 캐시 유효성 검사
   */
  private isCacheValid(regionInfo: RegionInfo): boolean {
    const now = new Date().getTime();
    const cachedTime = regionInfo.detectedAt.getTime();
    return now - cachedTime < REGION_CACHE.DURATION_MS;
  }

  /**
   * IP 정규화 - IPv6 매핑된 IPv4 주소 처리
   */
  private normalizeIp(ip: string): string {
    // ::ffff:192.168.1.1 형태의 IPv6 매핑된 IPv4 주소 처리
    if (ip.includes('::ffff:')) {
      return ip.replace('::ffff:', '');
    }
    return ip;
  }

  /**
   * 사용자 IP 설정 (서버사이드에서 호출)
   */
  setUserIP(ip: string): void {
    this.userIP = this.normalizeIp(ip);
  }

  /**
   * IP 기반 리전 감지
   * 브라우저 환경에서는 API 호출, 서버 환경에서는 geoip-lite 사용
   * ✅ P0-4: Added retry logic with exponential backoff
   */
  private async detectFromIP(): Promise<RegionInfo | null> {
    // 브라우저 환경: 항상 API를 통해 서버에서 리전 정보 가져오기
    if (typeof window !== 'undefined') {
      try {
        return await retry(
          async () => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), REGION_API.TIMEOUT_MS);

            try {
              const response = await fetch(REGION_API.ENDPOINT, {
                signal: controller.signal,
              });

              clearTimeout(timeoutId);

              if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
              }

              const data = (await response.json()) as DetectRegionResponse;
              const countryCode = data.countryCode ?? SUPPORTED_REGIONS.KOREA;
              logger.info(`[RegionDetector] API 응답: ${countryCode}`);

              return {
                countryCode,
                isOverseas: countryCode !== SUPPORTED_REGIONS.KOREA,
                detectedAt: new Date(),
              };
            } catch (error) {
              clearTimeout(timeoutId);
              throw error;
            }
          },
          {
            maxAttempts: REGION_API.RETRY_ATTEMPTS,
            initialDelay: REGION_API.RETRY_DELAY_MS,
            shouldRetry: isNetworkError,
          },
        );
      } catch (error) {
        // 모든 재시도 실패 시 null 반환 (캐시 저장 안 함 → 다음 로딩에서 재시도)
        logger.error('[RegionDetector] API 호출 실패 (모든 재시도 실패):', error);
        return null;
      }
    }

    // 서버 환경에서만 geoip-lite 사용 (dynamic import)
    if (typeof window === 'undefined' && this.userIP) {
      try {
        // 서버 환경에서만 geoip-lite를 동적으로 import
        const geoip = await import('geoip-lite');
        const geo = geoip.default.lookup(this.userIP);
        const countryCode = geo?.country ?? SUPPORTED_REGIONS.KOREA;
        logger.info(
          `[RegionDetector] geoip-lite 결과 - IP: ${this.userIP}, Country: ${countryCode}`,
        );

        return {
          countryCode,
          isOverseas: countryCode !== SUPPORTED_REGIONS.KOREA,
          detectedAt: new Date(),
        };
      } catch (error) {
        // geoip-lite를 로드할 수 없는 경우 기본값 사용
        logger.error('[RegionDetector] geoip-lite 로드 실패:', error);
        logger.info('[RegionDetector] 기본값(한국)으로 설정됩니다.');
      }
    }

    // 기본값: null (캐시 저장 안 함 → 다음 로딩에서 재시도)
    logger.info('[RegionDetector] IP 감지 실패 또는 정보 없음. null 반환.');
    return null;
  }
}

// 싱글톤 인스턴스
export const regionDetector = new RegionDetectorService();
